import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Fab,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AcUnit,
  Add,
  BakeryDining,
  CalendarMonth,
  Close,
  Delete,
  Edit,
  EmojiFoodBeverage,
  FreeBreakfast,
  LocalFlorist,
  LunchDining,
  Park,
  RestaurantMenu,
  Send,
  WarningAmber,
  WbSunny,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { RootState } from "store";
import { AuthState } from "store/features/auth";
import { getMenus, deleteMenuWeek, postMenuDay } from "apis";
import { IMenuWeek, MealType } from "types";
import MenuWeekDialog from "components/MenuWeekDialog";

const MEAL_TYPES: MealType[] = ["breakfast", "snack", "lunch", "tea_time"];
const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const SEASON_ICONS: Record<string, JSX.Element> = {
  spring: <LocalFlorist sx={{ fontSize: 18 }} />,
  summer: <WbSunny sx={{ fontSize: 18 }} />,
  autumn: <Park sx={{ fontSize: 18 }} />,
  winter: <AcUnit sx={{ fontSize: 18 }} />,
};

const MEAL_META: Record<MealType, { icon: JSX.Element; color: string }> = {
  breakfast: { icon: <FreeBreakfast />, color: "#F59E0B" },
  snack: { icon: <BakeryDining />, color: "#EC4899" },
  lunch: { icon: <LunchDining />, color: "#10B981" },
  tea_time: { icon: <EmojiFoodBeverage />, color: "#8B5CF6" },
};

const MenuPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const authInfo = useSelector<RootState, AuthState>((store) => store.auth);
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = authInfo.role === "NURSERY_ADMIN";

  const [weeks, setWeeks] = useState<IMenuWeek[]>([]);
  const [workingDays, setWorkingDays] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  const [year, setYear] = useState<number | "">("");
  const [season, setSeason] = useState<string>("");
  const [weekId, setWeekId] = useState<number | false>(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editItem, setEditItem] = useState<IMenuWeek | null>(null);
  const [deleteItem, setDeleteItem] = useState<IMenuWeek | null>(null);
  const [postDay, setPostDay] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    getMenus()
      .then((data) => {
        setWeeks(data.weeks);
        setWorkingDays(data.workingDays);
      })
      .catch((err) => enqueueSnackbar(err.message, { variant: "error" }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const years = useMemo(
    () => Array.from(new Set(weeks.map((w) => w.year))).sort((a, b) => b - a),
    [weeks],
  );
  const seasons = useMemo<string[]>(
    () =>
      Array.from(
        new Set(weeks.filter((w) => w.year === year).map((w) => w.season)),
      ),
    [weeks, year],
  );
  const weeksInScope = useMemo(
    () => weeks.filter((w) => w.year === year && w.season === season),
    [weeks, year, season],
  );

  // Keep the year/season/week selection valid as the data set changes.
  useEffect(() => {
    if (years.length && !years.includes(year as number)) setYear(years[0]);
  }, [years, year]);
  useEffect(() => {
    if (seasons.length && !seasons.includes(season)) setSeason(seasons[0]);
  }, [seasons, season]);
  useEffect(() => {
    if (!weeksInScope.some((w) => w.id === weekId)) {
      setWeekId(weeksInScope[0]?.id ?? false);
    }
  }, [weeksInScope, weekId]);

  const selectedWeek = weeksInScope.find((w) => w.id === weekId);

  const mealAt = (week: IMenuWeek, day: number, type: MealType) =>
    week.meals.find((m) => m.dayOfWeek === day && m.mealType === type);

  const confirmPost = useCallback(() => {
    if (!selectedWeek || postDay === null) return;
    setBusy(true);
    postMenuDay({ menuWeekId: selectedWeek.id, dayOfWeek: postDay })
      .then((res) =>
        enqueueSnackbar(t("menu.posted", { count: res.count }), {
          variant: "success",
        }),
      )
      .catch((err) => enqueueSnackbar(err.message, { variant: "error" }))
      .finally(() => {
        setBusy(false);
        setPostDay(null);
      });
  }, [selectedWeek, postDay, enqueueSnackbar, t]);

  const confirmDelete = useCallback(() => {
    if (!deleteItem) return;
    setBusy(true);
    deleteMenuWeek({ id: deleteItem.id })
      .then(() => {
        enqueueSnackbar(t("menu.deleted"), { variant: "success" });
        setDeleteItem(null);
        fetchData();
      })
      .catch((err) => enqueueSnackbar(err.message, { variant: "error" }))
      .finally(() => setBusy(false));
  }, [deleteItem, enqueueSnackbar, fetchData, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const pillSx = {
    flexWrap: "wrap",
    gap: 1,
    "& .MuiToggleButtonGroup-grouped": {
      m: 0,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "999px !important",
      px: 2,
      py: 0.6,
      gap: 0.75,
      textTransform: "none",
      fontWeight: 600,
      color: "text.secondary",
      "&.Mui-selected": {
        bgcolor: alpha(primary, 0.12),
        borderColor: alpha(primary, 0.5),
        color: primary,
        "&:hover": { bgcolor: alpha(primary, 0.2) },
      },
    },
  } as const;

  return (
    <>
      <Container sx={{ py: 3 }}>
        {weeks.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={10}
          >
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(primary, 0.08),
                mb: 2,
              }}
            >
              <RestaurantMenu sx={{ fontSize: 44, color: primary }} />
            </Box>
            <Typography variant="h6" gutterBottom fontWeight={700}>
              {t("menu.empty")}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t("menu.empty_hint")}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Year + season selectors */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2.5,
                borderRadius: 3,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
              }}
            >
              <FormControl size="small">
                <Select
                  value={year === "" ? "" : year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  renderValue={(v) => (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarMonth
                        sx={{ fontSize: 18, color: "text.secondary" }}
                      />
                      <span>{v as number}</span>
                    </Box>
                  )}
                  sx={{
                    borderRadius: "999px",
                    fontWeight: 600,
                    bgcolor: "background.paper",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                  }}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={season}
                onChange={(_, v) => v && setSeason(v)}
                sx={pillSx}
              >
                {seasons.map((s) => (
                  <ToggleButton key={s} value={s}>
                    {SEASON_ICONS[s]}
                    {t(`menu.seasons.${s}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Paper>

            {/* Week pills */}
            {weeksInScope.length > 0 && weekId !== false && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={weekId}
                onChange={(_, v) => v !== null && setWeekId(v)}
                sx={{ ...pillSx, mb: 2.5 }}
              >
                {weeksInScope.map((w) => (
                  <ToggleButton key={w.id} value={w.id}>
                    {w.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            {selectedWeek && (
              <Paper
                variant="outlined"
                sx={{ borderRadius: 3, overflow: "hidden" }}
              >
                {isAdmin && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={2}
                    py={1.25}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      bgcolor: alpha(primary, 0.04),
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {selectedWeek.label}
                    </Typography>
                    <Box>
                      <Tooltip title={t("labels.edit")}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditItem(selectedWeek);
                            setOpenDialog(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("buttons.delete")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteItem(selectedWeek)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}

                <TableContainer>
                  <Table
                    size="small"
                    sx={{
                      tableLayout: "fixed",
                      minWidth: 760,
                      "& td, & th": { borderColor: "divider" },
                    }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: alpha(theme.palette.text.primary, 0.02),
                        }}
                      >
                        <TableCell
                          sx={{
                            width: 140,
                            fontWeight: 700,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            fontSize: 11,
                            letterSpacing: 0.5,
                          }}
                        >
                          {t("menu.meal")}
                        </TableCell>
                        {workingDays.map((day) => (
                          <TableCell key={day} align="center">
                            <Stack alignItems="center" spacing={0.5}>
                              <Typography variant="body2" fontWeight={700}>
                                {t(`menu.weekdays.${DAY_KEYS[day]}`)}
                              </Typography>
                              {isAdmin && (
                                <Tooltip title={t("menu.post")}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={busy}
                                      onClick={() => setPostDay(day)}
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        color: primary,
                                        bgcolor: alpha(primary, 0.1),
                                        "&:hover": {
                                          bgcolor: alpha(primary, 0.22),
                                        },
                                      }}
                                    >
                                      <Send sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {MEAL_TYPES.map((type) => {
                        const meta = MEAL_META[type];
                        return (
                          <TableRow
                            key={type}
                            sx={{ "&:hover": { bgcolor: "action.hover" } }}
                          >
                            <TableCell>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1.25}
                              >
                                <Box
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: 2,
                                    display: "grid",
                                    placeItems: "center",
                                    color: meta.color,
                                    bgcolor: alpha(meta.color, 0.12),
                                    "& svg": { fontSize: 19 },
                                  }}
                                >
                                  {meta.icon}
                                </Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {t(type)}
                                </Typography>
                              </Box>
                            </TableCell>
                            {workingDays.map((day) => {
                              const meal = mealAt(selectedWeek, day, type);
                              const desc = meal?.description;
                              const alt = meal?.dietaryDescription;
                              return (
                                <TableCell
                                  key={day}
                                  align="center"
                                  sx={{
                                    direction: "auto",
                                    unicodeBidi: "plaintext",
                                    color: desc
                                      ? "text.primary"
                                      : "text.disabled",
                                  }}
                                >
                                  {desc || "—"}
                                  {alt && (
                                    <Box
                                      mt={0.5}
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        bgcolor: alpha(
                                          theme.palette.success.main,
                                          0.1,
                                        ),
                                        color: "success.dark",
                                      }}
                                    >
                                      <LocalFlorist sx={{ fontSize: 14 }} />
                                      <Typography variant="caption">
                                        {alt}
                                      </Typography>
                                    </Box>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 600, verticalAlign: "top" }}
                        >
                          {t("menu.dietary")}
                        </TableCell>
                        {workingDays.map((day) => {
                          const dm = selectedWeek.days?.find(
                            (d) => d.dayOfWeek === day,
                          );
                          const hasInfo =
                            dm && (dm.vegetarian || dm.allergens?.length);
                          return (
                            <TableCell
                              key={day}
                              align="center"
                              sx={{ verticalAlign: "top" }}
                            >
                              {hasInfo ? (
                                <Box
                                  display="flex"
                                  flexWrap="wrap"
                                  gap={0.5}
                                  justifyContent="center"
                                >
                                  {dm?.vegetarian && (
                                    <Chip
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      icon={<LocalFlorist />}
                                      label={t("menu.vegetarian")}
                                    />
                                  )}
                                  {dm?.allergens?.map((a) => (
                                    <Chip
                                      key={a}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      label={t(`menu.allergen_list.${a}`)}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Container>

      {isAdmin && (
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            pl: 2.5,
            pr: 3,
            gap: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 3,
          }}
          onClick={() => {
            setEditItem(null);
            setOpenDialog(true);
          }}
        >
          <Add />
          {t("menu.add")}
        </Fab>
      )}

      {openDialog && (
        <MenuWeekDialog
          open={openDialog}
          editItem={editItem}
          workingDays={workingDays}
          onClose={(changed) => {
            setOpenDialog(false);
            setEditItem(null);
            if (changed) fetchData();
          }}
        />
      )}

      <Dialog
        open={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            color: "common.white",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <WarningAmber sx={{ fontSize: 24 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {t("menu.delete")}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteItem(null)}
            size="small"
            sx={{ color: "common.white" }}
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 3, textAlign: "center" }}>
          <Typography variant="body1" mb={1}>
            {t("alert.confirm_delete")}
          </Typography>
          {deleteItem && (
            <Typography variant="body2" color="text.secondary">
              <strong>{deleteItem.label}</strong>
            </Typography>
          )}
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={() => setDeleteItem(null)}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            fullWidth
            disabled={busy}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {busy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("buttons.delete")
            )}
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={postDay !== null}
        onClose={() => setPostDay(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: "common.white",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Send sx={{ fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {t("menu.post")}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setPostDay(null)}
            size="small"
            sx={{ color: "common.white" }}
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 3, textAlign: "center" }}>
          <Typography variant="body1">
            {t("menu.post_confirm", {
              day:
                postDay !== null ? t(`menu.weekdays.${DAY_KEYS[postDay]}`) : "",
            })}
          </Typography>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={() => setPostDay(null)}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            onClick={confirmPost}
            variant="contained"
            fullWidth
            disabled={busy}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {busy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("menu.post")
            )}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default MenuPage;
